import logger from './logger';
/**
 * Domain Utilities - Centralized domain detection and routing logic
 * Consolidates all domain-specific logic to prevent inconsistencies
 */

export class DomainUtils {
  static getHostname() {
    return window.location.hostname;
  }

  static getOrigin() {
    return window.location.origin;
  }

  static isLocalhost() {
    const hostname = this.getHostname();
    return hostname === 'localhost' || hostname.includes('127.0.0.1') || hostname.includes('localhost');
  }

  static isProduction() {
    return !this.isLocalhost();
  }

  static getDomainType() {
    const hostname = this.getHostname();
    
    if (this.isLocalhost()) {
      // On localhost, detect admin app by port 3017 or subdomain env
      const port = window.location.port;
      if (port === '3017' || window.ADMIN_APP || process.env.REACT_APP_SUBDOMAIN === 'admin') {
        return 'admin';
      }
      if (port === '3014' || window.ACCOUNTS_APP || process.env.REACT_APP_SUBDOMAIN === 'accounts') {
        return 'accounts';
      }
      
      // Default to mail for other localhost
      return 'mail';
    }
    
    if (hostname === 'accounts.aiinak.com') {
      return 'accounts';
    }
    
    if (hostname === 'admin.aiinak.com') {
      return 'admin';
    }
    
    if (hostname === 'mail.aiinak.com') {
      return 'mail';
    }
    
    if (hostname === 'drive.aiinak.com') {
      return 'drive';
    }
    
    if (hostname === 'meetings.aiinak.com') {
      return 'meetings';
    }
    
    // Default fallback for any other domain
    return 'unknown';
  }

  static getDomainConfig() {
    const domainType = this.getDomainType();
    const isLocalhost = this.isLocalhost();
    
    return {
      type: domainType,
      isLocalhost,
      isProduction: this.isProduction(),
      hostname: this.getHostname(),
      origin: this.getOrigin(),
      baseDomain: isLocalhost ? '' : '.aiinak.com',
      protocol: isLocalhost ? 'http://' : 'https://',
      subdomains: ['mail', 'admin', 'accounts', 'drive', 'meetings'],
      
      // Domain-specific URLs
      urls: {
        accounts: isLocalhost ? (process.env.REACT_APP_ACCOUNTS_URL || 'http://localhost:3014') : (process.env.REACT_APP_ACCOUNTS_DOMAIN || 'https://accounts.aiinak.com'),
        admin: isLocalhost ? (process.env.REACT_APP_ADMIN_URL || 'http://localhost:3017') : (process.env.REACT_APP_ADMIN_DOMAIN || 'https://admin.aiinak.com'),
        mail: isLocalhost ? (process.env.REACT_APP_MAIL_URL || 'http://localhost:3000') : (process.env.REACT_APP_MAIL_DOMAIN || 'https://mail.aiinak.com'),
        drive: isLocalhost ? (process.env.REACT_APP_DRIVE_URL || 'http://localhost:3007') : (process.env.REACT_APP_DRIVE_DOMAIN || 'https://drive.aiinak.com'),
        meetings: isLocalhost ? (process.env.REACT_APP_MEETINGS_URL || 'http://localhost:3001') : (process.env.REACT_APP_MEETINGS_DOMAIN || 'https://meetings.aiinak.com')
      }
    };
  }

  static buildUrl(subdomain, path = '') {
    const config = this.getDomainConfig();
    
    if (!subdomain) {
      return config.urls.mail + (path.startsWith('/') ? path : '/' + path);
    }
    
    const baseUrl = config.urls[subdomain];
    
    if (!baseUrl) {
      logger.warn(`Unknown subdomain: ${subdomain}`);
      return config.urls.mail + (path.startsWith('/') ? path : '/' + path);
    }
    
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    return baseUrl + normalizedPath;
  }

  static getRedirectUrl(targetDomain, path = '') {
    return this.buildUrl(targetDomain, path);
  }

  static shouldUseSubdomainAuth() {
    return this.isProduction();
  }

  static getCookieDomain() {
    return this.isProduction() ? '.aiinak.com' : undefined;
  }

  static getCookieOptions() {
    const isProduction = this.isProduction();
    
    return {
      path: '/',
      domain: isProduction ? '.aiinak.com' : undefined,
      secure: isProduction,
      sameSite: isProduction ? 'None' : 'Lax'
    };
  }

  static isValidSubdomain(subdomain) {
    const validSubdomains = ['accounts', 'admin', 'mail', 'drive', 'meetings'];
    return Boolean(subdomain) && validSubdomains.includes(subdomain);
  }

  static isValidDomain(domain) {
    return this.isValidSubdomain(domain);
  }

  static getAppTypeForDomain() {
    const domainType = this.getDomainType();
    
    switch (domainType) {
      case 'localhost':
        return 'mail'; // Default to mail app for localhost
      case 'accounts':
        return 'accounts';
      case 'admin':
        return 'admin';
      case 'mail':
        return 'mail';
      case 'drive':
        return 'drive';
      case 'meetings':
        return 'meetings';
      default:
        return 'mail';
    }
  }

  static logDomainInfo() {
    const config = this.getDomainConfig();

    return config;
  }
}

export default DomainUtils;